import { AbnStatusEnum, GstStatusEnum } from '@sr-sdks/api-sdk-axios';
import { useState } from 'react';
import { workspaceService } from '../../../../reducers/workspaces/workspaceSlice';
import { useAppDispatch } from '../../../../stateStore';
import { shouldVerifyBusinessNumber } from '../../../../utils/helpers/common';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsDependencyContext } from '../../shared/types';

/**
 * Imperative ABN-lookup handler for the business-number field (R2).
 *
 * The lookup is event-triggered (Enter / Lookup button) and asynchronous, and
 * it writes several sibling fields. That does not fit the reactive
 * `useWatch`+`useEffect` dependency-hook shape, so it is modelled here as an
 * explicit imperative handler that calls `setValue` on siblings — an
 * intentional extension to the modular-form pattern (see research.md D4).
 *
 * Ported from packages/frontend/src/components/BusinessNumberInputFieldV2.tsx:54-75
 * (handleSearch: dispatch lookupBusinessNumber → on success set entityName/
 * gstStatus/abnStatus/verified/canProceed; on failure open the verify dialog).
 */
export const useBusinessNumberLookup = ({
  form,
}: WorkspaceDetailsDependencyContext) => {
  const dispatch = useAppDispatch();
  const { getValues, setValue } = form;
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);

  const lookup = async () => {
    // Re-entry guard: prevent concurrent lookups
    if (isLoading) return;

    const country = getValues(
      WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry,
    );
    const businessNumber = getValues(
      WORKSPACE_DETAILS_FORM_FIELDS.businessNumber,
    );

    if (!shouldVerifyBusinessNumber(country) || !businessNumber) return;

    // Capture the requested business number before async operation
    const requested = businessNumber;

    try {
      setIsLoading(true);
      const result = await dispatch(
        workspaceService.lookupBusinessNumber(businessNumber),
      );

      // Stale-result check: verify business number hasn't changed during the lookup
      if (
        getValues(WORKSPACE_DETAILS_FORM_FIELDS.businessNumber) !== requested
      ) {
        return;
      }

      if (workspaceService.lookupBusinessNumber.fulfilled.match(result)) {
        setValue(
          WORKSPACE_DETAILS_FORM_FIELDS.entityName,
          result.payload.entityName ?? '',
          { shouldDirty: true },
        );

        // Enum/null guard: coerce gstStatus to undefined if not a valid GstStatusEnum value
        const gstStatus = Object.values(GstStatusEnum).includes(
          result.payload.gstStatus,
        )
          ? result.payload.gstStatus
          : undefined;

        setValue(WORKSPACE_DETAILS_FORM_FIELDS.gstStatus, gstStatus);

        // Enum/null guard: coerce abnStatus to undefined if not a valid AbnStatusEnum value
        const abnStatus = Object.values(AbnStatusEnum).includes(
          result.payload.abnStatus,
        )
          ? result.payload.abnStatus
          : undefined;

        setValue(WORKSPACE_DETAILS_FORM_FIELDS.abnStatus, abnStatus);

        setValue(WORKSPACE_DETAILS_FORM_FIELDS.businessNumberVerified, true, {
          shouldDirty: true,
        });
        setValue(WORKSPACE_DETAILS_FORM_FIELDS.canProceed, true, {
          shouldValidate: true,
        });
      } else {
        setValue(WORKSPACE_DETAILS_FORM_FIELDS.businessNumberVerified, false);
        setIsVerifyDialogOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** User chose to proceed with an unverified business number. */
  const proceedUnverified = () => {
    setIsVerifyDialogOpen(false);
    setValue(WORKSPACE_DETAILS_FORM_FIELDS.canProceed, true, {
      shouldValidate: true,
    });
  };

  return {
    isLoading,
    isVerifyDialogOpen,
    lookup,
    proceedUnverified,
    setIsVerifyDialogOpen,
  };
};
